import { AxiosResponse } from 'axios';
import {
  PROFILE_ROYALTIES_LAST_MONTH_MOCK,
  PROFILE_ROYALTIES_LAST_WEEK_MOCK,
  PROFILE_ROYALTIES_LAST_YEAR_MOCK,
} from 'mocks/profile-royalties.mock';
import {
  isRoyaltyMockFailureState,
  ROYALTY_CLAIMABLE_RMV_MOCK,
  ROYALTY_FAILURE_DETAIL,
  ROYALTY_FAILURE_STATUS,
  ROYALTY_PREVIEW_MOCKS,
} from 'mocks/royalty-points.mocks';

import { IChartData } from 'models/i-chart-data.interface';
import { IPassiveIncome } from 'models/passive-income/i-passive-income.interface';
import { IVipRoyaltyPointsPreview } from 'models/vip/i-vip-royalty-points.interface';

import { Duration } from 'enums/duration.enum';

import { getRoyaltyMockState } from 'utils/dev-royalty-mocks.utils';

import { getApiAxiosRequestMock } from './mocks.service';

/**
 * The mock data source for the royalty → VIP Points perk. A sibling of `mocks.service.ts` rather
 * than an extension of it: that module is a flat list of one-line `getXMock()` wrappers over a
 * fixture, and this layer needs a state selector, two different providers and — for two of its
 * states — a REJECTION rather than a payload. Kept apart, `mocks.service.ts` keeps its role and
 * this file can be read as the one place the perk is faked. Its `getApiAxiosRequestMock` is reused
 * below, so the success shape and the artificial delay still come from the shared helper.
 *
 * WHETHER the mock runs, and WHICH state it serves, both come from
 * `utils/dev-royalty-mocks.utils` — a `sessionStorage` key the `DevDebugHeader` pull-down writes,
 * plus a non-production build. Nothing here re-checks the guard; the two call sites
 * (`vip-royalty-points.service.ts`, `passive-income.service.ts`) ask once, at the top.
 *
 * The state is read fresh on every call, so it follows whatever the panel last committed. That is
 * the point of doing this client-side at all: the backend's canned-totals env var could show one
 * state and needed a server restart to show a different one.
 *
 * Both providers are mocked because two of the three surfaces are invisible without the second.
 * The preview endpoint drives the VIP page panel and the claim modal's estimate; the passive-income
 * proxy drives `availableToCollect`, and at 0 the profile card hides its note and disables the
 * Claim button, so the modal cannot even be opened to look at the estimate.
 */

/** Long enough to see a loading state, short enough not to be in the way. */
const MOCK_DELAY_MS = 200;

/**
 * An axios-shaped rejection. Only `response.status` is actually read — by
 * `useVipRoyaltyPointsPreviewQuery`, which splits 404 (`featureOff`) from everything else
 * (`unavailable`) and refuses to retry anything under 500 — but the body and message are the real
 * ones so a network panel and a console reads the same as against a live backend.
 */
const rejectAfterDelay = (status: number, detail: string): Promise<never> =>
  new Promise((_, reject) =>
    setTimeout(
      () =>
        reject(
          Object.assign(new Error(`Request failed with status code ${status}`), {
            isAxiosError: true,
            response: { status, data: { detail }, statusText: '', headers: {}, config: {} },
          })
        ),
      MOCK_DELAY_MS
    )
  );

/**
 * `GET /api/v1/vip/royalty-points/preview/`.
 *
 * `feature_off` and `unavailable` reproduce the FAILURE, not a payload with a flag on it. The hook
 * decides what those states mean from the HTTP status alone, so a mock that resolved with a body
 * would exercise neither branch and would leave the two surfaces looking identical to `empty` —
 * which is the exact confusion this whole layer exists to remove.
 */
export const getVipRoyaltyPointsPreviewMock = (): Promise<AxiosResponse<IVipRoyaltyPointsPreview>> => {
  const state = getRoyaltyMockState();

  if (isRoyaltyMockFailureState(state)) {
    return rejectAfterDelay(ROYALTY_FAILURE_STATUS[state], ROYALTY_FAILURE_DETAIL[state]);
  }

  return getApiAxiosRequestMock(ROYALTY_PREVIEW_MOCKS[state], false, MOCK_DELAY_MS);
};

/**
 * `GET /express-proxy/insight/profile-passive-income/` — the whole claimable balance.
 *
 * Deliberately the SAME figure in every state. This is a different provider from the preview, and
 * it does not care whether the perk is switched off, down, or paying this holder nothing: a wallet
 * with royalty to claim has royalty to claim. Holding it constant is also what makes `feature_off`
 * and `unavailable` worth looking at — the card still shows a balance, and the question is whether
 * the perk's note correctly stays away from it.
 */
export const getProfilePassiveIncomeMock = (): Promise<IPassiveIncome> =>
  new Promise((resolve) => setTimeout(() => resolve({ income: ROYALTY_CLAIMABLE_RMV_MOCK }), MOCK_DELAY_MS));

/**
 * The claim modal's royalties chart, which comes from the same dead-locally insight proxy. Reuses
 * the fixtures `ProfileRoyaltiesModal` already ships for its own hardcoded `USE_MOCK_DATA` switch,
 * so there is one set of chart numbers rather than two that can disagree.
 *
 * A fresh array each call: `PassiveIncomeChart` re-renders off the reference, so handing back the
 * shared fixture would make a filter change look like nothing happened.
 */
export const getProfilePassiveIncomeChartMock = (duration: Duration): IChartData[] => {
  switch (duration) {
    case Duration.weeks:
    case Duration.days:
      return [...PROFILE_ROYALTIES_LAST_WEEK_MOCK];
    case Duration.months:
      return [...PROFILE_ROYALTIES_LAST_MONTH_MOCK];
    case Duration.years:
    default:
      return [...PROFILE_ROYALTIES_LAST_YEAR_MOCK];
  }
};
